import { createContext, useContext, useEffect, useReducer } from 'react';
import type { ReactNode } from 'react';
import { SET_AUTH_CONFIG, SET_AUTH_USER, USER_LOGOUT } from './actionTypes';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { getAuthConfig } from '../actions/org';
import calculateUserRoles from '../util/calculateUserRoles';
import { recoverExpiredSession } from '@/util/sessionRecovery';
import type { AuthState, AuthAction, Organization, AppRole } from '@/types';

const AuthContext = createContext<AuthState | null>(null);

const AuthDispatchContext = createContext<React.Dispatch<AuthAction> | null>(
  null,
);

const initialState: AuthState = {
  config: null,
  user_manager: null,
  user: undefined,
  roles: [],
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [auth, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    if (!auth.config) {
      getAuthConfig().then((config: Organization) => {
        dispatch({ type: SET_AUTH_CONFIG, payload: config });
      });
    }
  }, [auth.config, dispatch]);

  useEffect(() => {
    if (!auth.user_manager || auth.user !== undefined) return;

    const user_manager = auth.user_manager;
    let cancelled = false;

    const bootstrapSession = async () => {
      try {
        const stored_user = await user_manager.getUser();
        if (cancelled) return;

        if (!stored_user) {
          dispatch({ type: SET_AUTH_USER, payload: null });
          return;
        }

        const is_expired =
          stored_user.expired ||
          (stored_user.expires_at != null &&
            stored_user.expires_at < Date.now() / 1000);

        if (!is_expired) {
          dispatch({ type: SET_AUTH_USER, payload: stored_user });
          return;
        }

        const refreshed_user = await recoverExpiredSession(user_manager);
        if (cancelled) return;

        if (refreshed_user?.access_token) {
          dispatch({ type: SET_AUTH_USER, payload: refreshed_user });
        } else {
          dispatch({ type: USER_LOGOUT });
        }
      } catch (error) {
        console.log('auth get user failed', error);
        if (!cancelled) {
          dispatch({ type: SET_AUTH_USER, payload: null });
        }
      }
    };

    void bootstrapSession();

    return () => {
      cancelled = true;
    };
  }, [auth.user_manager, auth.user]);

  return (
    <AuthContext.Provider value={auth}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return auth;
}

export function useAuthDispatch(): React.Dispatch<AuthAction> {
  const dispatch = useContext(AuthDispatchContext);
  if (!dispatch) {
    throw new Error('useAuthDispatch must be used within AuthProvider');
  }
  return dispatch;
}

function authReducer(auth: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case SET_AUTH_CONFIG: {
      // Filter out null/undefined values and only include fields UserManager needs
      const settings: Record<string, unknown> = {
        userStore: new WebStorageStateStore({ store: window.localStorage }),
        loadUserInfo: true,
        automaticSilentRenew: false,
        accessTokenExpiringNotificationTimeInSeconds: 300,
        monitorSession: false,
      };

      // Only add fields that are not null/undefined
      if (action.payload['authority'])
        settings['authority'] = action.payload['authority'];
      if (action.payload['client_id'])
        settings['client_id'] = action.payload['client_id'];
      if (action.payload['redirect_uri'])
        settings['redirect_uri'] = action.payload['redirect_uri'];
      if (action.payload['response_type'])
        settings['response_type'] = action.payload['response_type'];
      if (action.payload['scope']) settings['scope'] = action.payload['scope'];
      if (action.payload['post_logout_redirect_uri'])
        settings['post_logout_redirect_uri'] =
          action.payload['post_logout_redirect_uri'];
      if (action.payload['response_mode'])
        settings['response_mode'] = action.payload['response_mode'];

      const userManager = new UserManager(settings as any);

      return {
        ...auth,
        user_manager: userManager,
        config: { ...action.payload },
      };
    }
    case SET_AUTH_USER: {
      if (action.payload && action.payload?.access_token) {
        localStorage.setItem(
          'accqsure_jwt',
          action.payload && action.payload?.access_token,
        );
      }
      // console.log(action.payload);
      const roles: AppRole[] = calculateUserRoles(action.payload);
      return { ...auth, user: action.payload, roles };
    }
    case USER_LOGOUT: {
      localStorage.removeItem('accqsure_jwt');
      return { ...auth, user: null, roles: [] };
    }
    default: {
      throw Error('Unknown action: ' + JSON.stringify(action));
    }
  }
}
